/**
 * @file useBinLabelPrintForm.ts
 * @description Состояние диалога печати этикеток ячеек (phase19.22 шаг 5):
 *   копии, шаблон, этикетки полок/стеллажа, «начать с N», коды, отправка и
 *   результат. Диалог — раскладка, хук — правила (SRP), расчёты — в
 *   `bin-label-print.ts`.
 *
 * Отдаёт те же поля сводки, что и форма печати товара (`summary`,
 * `startAt`, `setStartAt`, `overLimit`) — поэтому `LabelPrintSummary`
 * переиспользуется без изменений в поведении.
 *
 * Из калибровки склада берём только «начать с N»: режим кодов склада
 * относится к товарным этикеткам и сюда не наследуется (адреса — BOTH).
 * Стеллаж целиком — по умолчанию с этикетками полок и стеллажа, как сервер.
 */

import { useEffect, useRef, useState } from "react";

import {
  BIN_LABEL_DEFAULT_TEMPLATE,
  BIN_LABEL_MAX_LABELS,
  binLabelErrorText,
  binLabelLines,
  buildBinLabelPrintInput,
  type BinLabelScope,
} from "./bin-label-print";
import {
  labelsApi,
  type BinLabelPrintResult,
  type BinLabelTemplate,
  type LabelCodeMode,
  type LabelStartAt,
  type LabelTemplate,
} from "../../../lib/api-labels";
import type { StorageBinDto } from "../../../lib/api-bins";
import { summarizeSheets } from "../../../lib/label-sheets";
import { useLabelCutLines } from "../labels/useLabelCutLines";

interface Params {
  open: boolean;
  warehouseId: string;
  selected: StorageBinDto[];
  scope: BinLabelScope;
  onPrinted?: (result: BinLabelPrintResult) => void;
}

export function useBinLabelPrintForm({ open, warehouseId, selected, scope, onPrinted }: Params) {
  const [copies, setCopies] = useState(1);
  const [template, setTemplate] = useState<BinLabelTemplate>(BIN_LABEL_DEFAULT_TEMPLATE);
  const [includeShelfLabels, setIncludeShelfLabels] = useState(false);
  const [includeRackLabel, setIncludeRackLabel] = useState(false);
  const [codeMode, setCodeMode] = useState<LabelCodeMode>("BOTH");
  // Линии реза — настройка рабочего места (бумага в принтере), не сбрасываем.
  const [cutLines, setCutLines] = useLabelCutLines();
  const [startAt, setStartAtState] = useState<LabelStartAt>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<{ text: string; retryable: boolean } | null>(null);
  const [result, setResult] = useState<BinLabelPrintResult | null>(null);
  const wasOpen = useRef(false);

  // Открытие — чистый лист; стеллаж целиком — сразу предлагаем его этикетку.
  useEffect(() => {
    const justOpened = open && !wasOpen.current;
    wasOpen.current = open;
    if (!justOpened) return;
    setResult(null);
    setError(null);
    setCopies(1);
    setTemplate(BIN_LABEL_DEFAULT_TEMPLATE);
    setIncludeRackLabel(scope.rack !== null);
    setIncludeShelfLabels(scope.rack !== null);
    setCodeMode("BOTH");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- только в момент открытия
  }, [open]);

  // Калибровка склада: продолжаем начатый лист (коды склада — не наши).
  useEffect(() => {
    if (!open || !warehouseId) return;
    let cancelled = false;
    labelsApi
      .labelSheets(warehouseId)
      .then((res) => {
        if (cancelled) return;
        setStartAtState(Object.fromEntries(res.sheets.map((s) => [s.template, s.nextStartAt])));
      })
      .catch(() => {
        // Без калибровки печать работает: «начать с 1».
        if (!cancelled) setStartAtState({});
      });
    return () => {
      cancelled = true;
    };
  }, [open, warehouseId]);

  const options = { copies, template, includeShelfLabels, includeRackLabel, codeMode, startAt, cutLines };
  const summary = summarizeSheets(binLabelLines(selected, options), startAt);
  const overLimit = summary.totalLabels > BIN_LABEL_MAX_LABELS;
  const canSubmit = selected.length > 0 && !overLimit && !submitting;

  async function submit(): Promise<void> {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await labelsApi.printBins(buildBinLabelPrintInput(warehouseId, scope, options));
      setResult(res);
      // «Напечатать ещё» продолжит начатые листы с того места, где закончили.
      setStartAtState((prev) => ({
        ...prev,
        ...Object.fromEntries(res.perTemplate.map((p) => [p.template, p.nextStartAt])),
      }));
      onPrinted?.(res);
    } catch (err) {
      setError(binLabelErrorText(err));
    } finally {
      setSubmitting(false);
    }
  }

  return {
    copies,
    setCopies,
    template,
    setTemplate,
    includeShelfLabels,
    setIncludeShelfLabels,
    includeRackLabel,
    setIncludeRackLabel,
    codeMode,
    setCodeMode,
    cutLines,
    setCutLines,
    startAt,
    setStartAt: (t: LabelTemplate, value: number) => setStartAtState((prev) => ({ ...prev, [t]: value })),
    summary,
    overLimit,
    maxTotal: BIN_LABEL_MAX_LABELS,
    canSubmit,
    submitting,
    error,
    result,
    submit,
    backToForm: () => setResult(null),
  };
}

export type BinLabelPrintForm = ReturnType<typeof useBinLabelPrintForm>;
